import { createContext, useContext, useEffect, useState } from 'react';

/**
 * Source of `navigate(view:<viewId>, …)` locations for inline-dom views
 * (架构方案 §5.8 跳转, 见 8). Implemented by the app shell (which listens for
 * the main process's `hibro:view:navigate` fan-out and buffers the latest
 * location per view so one that arrived before the view mounted is delivered on
 * mount). The platform package consumes it via {@link NavigationContext} so the
 * platform stays free of the preload bridge.
 */
export interface NavigationSource {
  /**
   * Subscribe to navigations targeting one view *instance*; the listener receives
   * the opaque location. Returns a disposer.
   *
   * `instanceKey` is part of the address (§5.7): a navigation aimed at one
   * document must not re-render the tab showing another.
   */
  subscribe(
    viewId: string,
    instanceKey: string | undefined,
    listener: (location: unknown) => void,
  ): () => void;
  /** The latest location targeting that view instance, or `undefined` if none yet. */
  current(viewId: string, instanceKey?: string): unknown;
  /**
   * Subscribe to every navigation regardless of target (架构方案 §5.8 跳转,
   * 见 8). Lets the workbench implement `present(面)` — open/select the
   * navigated view instance's Tab and focus it before the location (点) is
   * delivered to the now-mounted view, so `navigate(view:<id>[::<key>], …)` is
   * "open + locate" atomic (Web Router / VSCode `showTextDocument` mental model).
   * Returns a disposer.
   */
  subscribeAll(
    listener: (
      viewId: string,
      location: unknown,
      instanceKey: string | undefined,
    ) => void,
  ): () => void;
}

/**
 * Provides the {@link NavigationSource} to inline-dom views (见 8). The app
 * shell wraps `Workbench`/`DetachedSurface` with this provider; `undefined`
 * (the default) disables view navigation gracefully (e.g. headless/tests).
 */
export const NavigationContext = createContext<NavigationSource | undefined>(
  undefined,
);

/**
 * The latest navigation location for one view instance (架构方案 §5.8 跳转,
 * 见 8), re-rendering the view when a new location arrives. Reads the buffered
 * latest on mount (so a location delivered before mount is honored) and
 * subscribes for subsequent ones. Returns `undefined` when no navigation source
 * is provided or that instance has not been navigated.
 */
export function useViewNavigation(
  viewId: string,
  instanceKey?: string,
): unknown {
  const source = useContext(NavigationContext);
  const [location, setLocation] = useState<unknown>(() =>
    source?.current(viewId, instanceKey),
  );
  useEffect(() => {
    if (!source) {
      return;
    }
    setLocation(source.current(viewId, instanceKey));
    return source.subscribe(viewId, instanceKey, (next) => setLocation(next));
  }, [source, viewId, instanceKey]);
  return location;
}
